import { useJourneyStore } from '@/stores/journeyStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { CheckCircle2, Circle, Compass, Settings2, FileText, Box } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { JourneyPhaseWithMode } from '@ispot/shared'

interface JourneyProgressCardProps {
  expandedPhaseId: string | null
  onTogglePhase: (phaseId: string) => void
}

export function JourneyProgressCard({ expandedPhaseId, onTogglePhase }: JourneyProgressCardProps) {
  const { setShowGoalWizard } = useWorkspaceStore()
  const {
    getSelectedGoal,
    getActivePhases,
    getCurrentPhase,
    getPhaseProgress,
    getOverallProgress,
    isActivityCompleted,
    isArtifactCompleted,
  } = useJourneyStore()

  const selectedGoal = getSelectedGoal()
  const phases = getActivePhases()
  const currentPhase = getCurrentPhase()

  if (!selectedGoal) {
    return (
      <section aria-label="Journey" className="rounded-xl bg-white border border-warm-200 p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Compass className="w-5 h-5 text-text-muted shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-text">No journey selected</div>
              <div className="text-sm text-text-muted mt-0.5">
                Choose a journey goal to get guided recommendations
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowGoalWizard(true)}
            className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-[var(--color-accent)] rounded-lg hover:bg-[var(--color-accent-700)] transition-colors"
          >
            Select journey
          </button>
        </div>
      </section>
    )
  }

  const expandedPhase: JourneyPhaseWithMode | null = expandedPhaseId
    ? phases.find(p => p.id === expandedPhaseId) ?? null
    : null

  const overallProgress = Math.round(getOverallProgress())

  return (
    <section aria-label="Journey" className="rounded-xl bg-white border border-warm-200 p-6">
      <div className="flex items-baseline justify-between gap-4 mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted">
          Phases
        </div>
        <div className="flex items-center gap-4">
          <div className="text-[12px] tabular-nums text-text-secondary">
            Journey · <span className="font-semibold text-text">{overallProgress}%</span>
          </div>
          <button
            onClick={() => setShowGoalWizard(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-text-secondary hover:text-text hover:bg-warm-100 rounded-lg transition-colors"
          >
            <Settings2 className="w-3 h-3" />
            Change
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {phases.map(phase => {
          const progress = getPhaseProgress(phase.id)
          const isCurrent = phase.id === currentPhase?.id
          const isCompleted = progress >= 100
          const isExpanded = expandedPhaseId === phase.id
          const fillWidth = `${Math.max(0, Math.min(progress, 100))}%`

          let metaText: string
          if (isCompleted) metaText = 'Done'
          else if (progress > 0) metaText = `${Math.round(progress)}%`
          else metaText = 'Not started'

          return (
            <button
              key={phase.id}
              onClick={() => onTogglePhase(phase.id)}
              aria-expanded={isExpanded}
              aria-label={`${phase.name}, ${Math.round(progress)} percent complete`}
              className={cn(
                'group flex flex-col items-stretch text-left rounded-md px-2 py-1.5 -mx-2 transition-colors',
                'hover:bg-warm-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
                isExpanded && 'bg-warm-100',
              )}
            >
              <div className="flex items-baseline gap-1.5">
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: phase.color, transform: 'translateY(-1px)' }}
                />
                <span className={cn(
                  'text-[13px] leading-tight truncate',
                  isCurrent || isCompleted ? 'text-text font-medium' : 'text-text-muted',
                )}>
                  {phase.name}
                </span>
              </div>

              <div
                className={cn(
                  'mt-2 h-1.5 rounded-full overflow-hidden relative',
                  isCompleted ? 'bg-text' : 'bg-warm-200',
                )}
                aria-hidden="true"
              >
                <span
                  className={cn(
                    'absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out',
                    isCurrent ? 'bg-[var(--color-accent)]' : 'bg-text',
                  )}
                  style={{ width: fillWidth }}
                />
              </div>

              <div className="mt-2 h-3.5 flex items-center gap-1.5 font-mono text-[10px] tabular-nums text-text-muted leading-none">
                {isCurrent && !isCompleted && (
                  <span className="inline-flex items-center gap-1 text-[var(--color-accent)] uppercase tracking-[0.05em] font-semibold">
                    <span className="w-1 h-1 rounded-full bg-[var(--color-accent)]" />
                    You are here
                  </span>
                )}
                {(!isCurrent || isCompleted) && (
                  <span className={isCompleted ? 'text-text-secondary' : undefined}>{metaText}</span>
                )}
                {isCurrent && !isCompleted && (
                  <span className="ml-auto">{Math.round(progress)}%</span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {expandedPhase && (
        <PhaseDetail
          phase={expandedPhase}
          isActivityCompleted={isActivityCompleted}
          isArtifactCompleted={isArtifactCompleted}
        />
      )}
    </section>
  )
}

interface PhaseDetailProps {
  phase: JourneyPhaseWithMode
  isActivityCompleted: (id: string) => boolean
  isArtifactCompleted: (id: string) => boolean
}

function PhaseDetail({ phase, isActivityCompleted, isArtifactCompleted }: PhaseDetailProps) {
  return (
    <div className="mt-4 pt-4 border-t border-warm-100">
      <div className="flex items-center gap-2.5 mb-1">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
          style={{ backgroundColor: phase.color }}
        >
          {phase.order}
        </div>
        <span className="font-display font-medium text-text">{phase.name}</span>
      </div>
      <p className="text-sm text-text-secondary mb-4 ml-[34px] leading-snug">{phase.objective}</p>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
            Activities ({phase.activities.length})
          </div>
          <div className="space-y-1.5">
            {phase.activities.map(activity => {
              const done = isActivityCompleted(activity.id)
              return (
                <div key={activity.id} className="flex items-start gap-2">
                  {done
                    ? <CheckCircle2 className="w-4 h-4 text-market mt-0.5 flex-shrink-0" />
                    : <Circle className="w-4 h-4 text-warm-300 mt-0.5 flex-shrink-0" />
                  }
                  <span className={cn(
                    'text-sm leading-snug',
                    done ? 'text-text-muted line-through' : 'text-text',
                  )}>
                    {activity.name}
                    {activity.isRequired && (
                      <span
                        className="text-[var(--color-danger-fg)] ml-0.5"
                        aria-label="Required"
                      >
                        *
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
            Artifacts ({phase.artifacts.length})
          </div>
          <div className="space-y-1.5">
            {phase.artifacts.map(artifact => {
              const done = isArtifactCompleted(artifact.id)
              const icon = artifact.type === 'canvas'
                ? <Box className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
                : <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
              return (
                <div key={artifact.id} className="flex items-start gap-2">
                  {icon}
                  <span className={cn(
                    'text-sm leading-snug',
                    done ? 'text-text-muted line-through' : 'text-text',
                  )}>
                    {artifact.name}
                    {artifact.isRequired && (
                      <span
                        className="text-[var(--color-danger-fg)] ml-0.5"
                        aria-label="Required"
                      >
                        *
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {phase.gate.criteria.length > 0 && (
        <div className="mt-4 pt-3 border-t border-warm-100">
          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
            Gate: {phase.gate.name}
          </div>
          <ul className="space-y-1.5">
            {phase.gate.criteria.map((criterion, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className="w-1 h-1 rounded-full bg-warm-300 mt-2 shrink-0"
                />
                <span className="text-sm text-text-secondary leading-snug">
                  {criterion}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
